import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { path } from 'ramda';
import uuid from 'uuid-random';

import { useFormatter } from '@core/js/hooks/useFormatter';
import { componentService } from '@core/js/services/component.service';
import { JACKPOTS_REDUCER } from '@core/js/store/constants';
import { useAppSelector, useCustomSelector } from '@core/js/store/hooks';

import { clsx } from '@interlayer/js/app/utils/clsx.util';

import { updateAmount } from './helpers/updateAmount';
import { useGetJackpot } from './hooks/useGetJackpot';
import { useIsVisibleJackpot } from './hooks/useIsVisibleJackpot';
import { useJackpotUpdateFrame } from './hooks/useJackpotUpdateFrame';
import { useUpdateJackpotData } from './hooks/useUpdateJackpotData';
import { TJackpot, TJackpotProps } from './types';

export const Jackpot: React.FC<TJackpotProps> = ({
    className,
    classname,
    jackpotCode,
    gameCode = '',
    gamePayId,
    interval = 100,
    styleCustom = {},
    setIsJackpotActive
}) => {
    const formatter = useFormatter();
    const [, setIsDataReady] = useState(false);
    const jackpotId = useRef(uuid());
    const jackpotCodeFromGame = useAppSelector(path(['gamesConfiguration', gameCode, 'jackpotCode']));
    const gamePayIdParsed = isNaN(Number(gamePayId)) ? undefined : Number(gamePayId);
    const code = (jackpotCode || jackpotCodeFromGame) + (gamePayIdParsed ? `_${gamePayIdParsed}` : '');
    const { jackpotStartTime, jackpots } = useCustomSelector(JACKPOTS_REDUCER);
    const jackpot = useMemo(() => jackpots?.[code], [code, jackpots]);
    const data = useRef<TJackpot>({ isReachLimit: false });
    const jackpotRef = useRef<HTMLDivElement>(null);
    const isVisible = useIsVisibleJackpot(jackpotRef.current);

    const update = useCallback(() => {
        const newState = updateAmount(data.current, interval);

        if (jackpotRef.current && Object.keys(newState).length) {
            data.current = { ...data.current, ...newState };

            jackpotRef.current.textContent = formatter.formatCurrency(data.current.amount, {
                currencyCode: data.current.currencyCode
            });
        }
    }, [formatter, interval]);

    useGetJackpot(code, gamePayIdParsed);
    useUpdateJackpotData(jackpot, gamePayIdParsed);
    useJackpotUpdateFrame(jackpot && isVisible && jackpotId.current, update);

    useEffect(() => {
        if (jackpot) {
            // for rerender after update data.
            setIsDataReady(true);

            if (setIsJackpotActive) {
                setIsJackpotActive(true);
            }

            data.current = {
                ...data.current,
                ...jackpot,
                amountStartTime: jackpotStartTime || Date.now()
            };
        }
    }, [jackpot, jackpotStartTime, setIsJackpotActive]);

    if (!jackpot) {
        return null;
    }

    return (
        <span
            ref={jackpotRef}
            style={styleCustom}
            className={clsx(className, classname)}
            data-jackpot-code={code}
        />
    );
};

componentService.registerWebContentComponents({ Jackpot });
